// Shipping methods, prices, and listed transit ranges reviewed against the store's General delivery profile on 2026-09-27.
// Do not infer a delivery date for methods whose transit range is not listed.
export const RATE_SNAPSHOT_DATE = '2026-09-27';

// Text in the shopper's language. snippets/sa-i18n-js.liquid sets window.SA_I18N on French, Spanish and German pages
// only; English pages have no dictionary, so every string below falls back to its English text unchanged.
export function saText(key, fallback, vars = null) {
  let text = globalThis.SA_I18N?.[key] ?? fallback;
  if (vars) for (const [name, value] of Object.entries(vars)) text = text.replaceAll(`{${name}}`, value);
  return text;
}

// A translated line that holds elements: "{free} Standard Shipping" → [<strong>FREE</strong>, " Standard Shipping"].
function saParts(text, nodes) {
  return text.split(/(\{\w+\})/).filter(Boolean).map(part => nodes[part.slice(1, -1)] ?? part);
}

const dateLocale = () => globalThis.SA_I18N?.date_locale || 'en-US';

// Checkout method names are English in the rate snapshot (carrier logos match on them); pages in other languages
// show them as "Livraison standard GLS". English pages keep the name as it is.
export function methodLabel(name) {
  const match = /^(.*) (Standard|Express|Postal) Shipping$/.exec(name);
  const template = match && globalThis.SA_I18N?.[`delivery.method_${match[2].toLowerCase()}`];
  return template ? template.replaceAll('{carrier}', match[1]) : name;
}
export const DELIVERY_ZONES = [
  {
    name: 'USA', countries: ['US'],
    methods: [
      // USA zone rate conditions: USPS is only offered up to $94.99, so from $95 checkout shows FedEx (free) and UPS only.
      {name: 'USPS Standard Shipping', kind: 'standard', price: 0, currency: 'USD', transit: [5, 10], availableBelow: 95},
      // Owner, 2026-10-18: FedEx Express is free from a threshold after discounts (USA zone rate conditions; the cart reward bar uses the same).
      // Thresholds since 2026-10-30 (owner): $95, £85, CA$135, €104, 800 DKK, 460 PLN, 1200 SEK, 2500 CZK, 40000 HUF, 550 RON.
      {name: 'FedEx Express Shipping', kind: 'express', price: 12, currency: 'USD', transit: [3, 5], priceRule: {freeFrom: 95, below: 12}},
      {name: 'UPS Express Shipping', kind: 'express', price: 22, currency: 'USD', transit: [3, 5]},
    ],
  },
  {
    name: 'UK', countries: ['GB'],
    methods: [
      {name: 'Evri Standard Shipping', kind: 'standard', price: 0, currency: 'GBP', transit: [3, 7]},
      {name: 'Royal Mail Standard Shipping', kind: 'standard', price: 0, currency: 'GBP', transit: [5, 10]},
      // Owner, 2026-10-21: FedEx Express is free from £75 after discounts (UK zone rate conditions; the cart reward bar uses the same £75).
      {name: 'FedEx Express Shipping', kind: 'express', price: 7, currency: 'GBP', transit: [3, 5], priceRule: {freeFrom: 85, below: 7}},
    ],
  },
  {
    name: 'Germany', countries: ['DE'],
    methods: [
      {name: 'GLS Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'Deutsche Post Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      // Owner, 2026-10-24: Europe (EU-27 + Monaco) gets free FedEx Express after discounts like the US/UK/Canada: €90 in the
      // 22 euro zones, 700 kr (DK), 400 zł (PL), 1 000 kr (SE), 2 200 Kč (CZ), 35 000 Ft (HU), 480 lei (RO) — each zone's
      // rate condition in the General profile; the cart reward bar's markets carry the same amounts. UPS and standard untouched.
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'France', countries: ['FR'],
    methods: [
      {name: 'GLS Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'La Poste Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Italy', countries: ['IT'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'Poste Italiane Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Spain', countries: ['ES'],
    methods: [
      {name: 'GLS Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'Correos Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Netherlands', countries: ['NL'],
    methods: [
      {name: 'GLS Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'PostNL Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Belgium', countries: ['BE'],
    methods: [
      {name: 'DHL Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'bpost Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Austria', countries: ['AT'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'Austrian Post Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Portugal', countries: ['PT'],
    methods: [
      {name: 'MRW Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'CTT Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Czechia', countries: ['CZ'],
    methods: [
      {name: 'Packeta Standard Shipping', kind: 'standard', price: 0, currency: 'CZK', transit: [3, 7]},
      {name: 'Česká pošta Postal Shipping', kind: 'standard', price: 0, currency: 'CZK', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 225, currency: 'CZK', transit: [3, 5], priceRule: {freeFrom: 2500, below: 225}},
      {name: 'UPS Express Shipping', kind: 'express', price: 300, currency: 'CZK', transit: [3, 5]},
    ],
  },
  {
    name: 'Bulgaria', countries: ['BG'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'Bulgarian Posts Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Croatia', countries: ['HR'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'Hrvatska pošta Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Estonia', countries: ['EE'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'Omniva Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Hungary', countries: ['HU'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'HUF', transit: [3, 7]},
      {name: 'Magyar Posta Postal Shipping', kind: 'standard', price: 0, currency: 'HUF', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 3400, currency: 'HUF', transit: [3, 5], priceRule: {freeFrom: 40000, below: 3400}},
      {name: 'UPS Express Shipping', kind: 'express', price: 4500, currency: 'HUF', transit: [3, 5]},
    ],
  },
  {
    name: 'Latvia', countries: ['LV'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'Latvijas Pasts Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Lithuania', countries: ['LT'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'Lietuvos paštas Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Luxembourg', countries: ['LU'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'POST Luxembourg Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Malta', countries: ['MT'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'MaltaPost Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Romania', countries: ['RO'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'RON', transit: [3, 7]},
      {name: 'Poșta Română Postal Shipping', kind: 'standard', price: 0, currency: 'RON', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 48, currency: 'RON', transit: [3, 5], priceRule: {freeFrom: 550, below: 48}},
      {name: 'UPS Express Shipping', kind: 'express', price: 64, currency: 'RON', transit: [3, 5]},
    ],
  },
  {
    name: 'Slovakia', countries: ['SK'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'Slovenská pošta Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Slovenia', countries: ['SI'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'Pošta Slovenije Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Monaco', countries: ['MC'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'La Poste Monaco Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Cyprus', countries: ['CY'],
    methods: [
      {name: 'Cyprus Post Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Greece', countries: ['GR'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'ELTA Hellenic Post Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Denmark', countries: ['DK'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'DKK', transit: [3, 7]},
      {name: 'PostNord Postal Shipping', kind: 'standard', price: 0, currency: 'DKK', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 67, currency: 'DKK', transit: [3, 5], priceRule: {freeFrom: 800, below: 67}},
      {name: 'UPS Express Shipping', kind: 'express', price: 90, currency: 'DKK', transit: [3, 5]},
    ],
  },
  {
    name: 'Finland', countries: ['FI'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'Posti Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Ireland', countries: ['IE'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [3, 7]},
      {name: 'An Post Postal Shipping', kind: 'standard', price: 0, currency: 'EUR', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 9, currency: 'EUR', transit: [3, 5], priceRule: {freeFrom: 104, below: 9}},
      {name: 'UPS Express Shipping', kind: 'express', price: 12, currency: 'EUR', transit: [3, 5]},
    ],
  },
  {
    name: 'Poland', countries: ['PL'],
    methods: [
      {name: 'DHL Standard Shipping', kind: 'standard', price: 0, currency: 'PLN', transit: [3, 7]},
      {name: 'Poczta Polska Postal Shipping', kind: 'standard', price: 0, currency: 'PLN', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 39, currency: 'PLN', transit: [3, 5], priceRule: {freeFrom: 460, below: 39}},
      {name: 'UPS Express Shipping', kind: 'express', price: 53, currency: 'PLN', transit: [3, 5]},
    ],
  },
  {
    name: 'Sweden', countries: ['SE'],
    methods: [
      {name: 'Widect Standard Shipping', kind: 'standard', price: 0, currency: 'SEK', transit: [3, 7]},
      {name: 'PostNord Postal Shipping', kind: 'standard', price: 0, currency: 'SEK', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 101, currency: 'SEK', transit: [3, 5], priceRule: {freeFrom: 1200, below: 101}},
      {name: 'UPS Express Shipping', kind: 'express', price: 135, currency: 'SEK', transit: [3, 5]},
    ],
  },
  {
    name: 'Bosnia & Herzegovina', countries: ['BA'],
    methods: [
      {name: 'BH Pošta Postal Shipping', kind: 'standard', price: 29, currency: 'BAM', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 57, currency: 'BAM', transit: [3, 5]},
      {name: 'UPS Express Shipping', kind: 'express', price: 63, currency: 'BAM', transit: [3, 5]},
    ],
  },
  {
    name: 'Iceland', countries: ['IS'],
    methods: [
      {name: 'Pósturinn Postal Shipping', kind: 'standard', price: 2055, currency: 'ISK', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 3973, currency: 'ISK', transit: [3, 5]},
      {name: 'UPS Express Shipping', kind: 'express', price: 4384, currency: 'ISK', transit: [3, 5]},
    ],
  },
  {
    name: 'Faroe Islands', countries: ['FO'],
    methods: [
      {name: 'Posta Postal Shipping', kind: 'standard', price: 112, currency: 'DKK', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 217, currency: 'DKK', transit: [3, 5]},
      {name: 'UPS Express Shipping', kind: 'express', price: 239, currency: 'DKK', transit: [3, 5]},
    ],
  },
  {
    name: 'Serbia', countries: ['RS'],
    methods: [
      {name: 'Pošta Srbije Postal Shipping', kind: 'standard', price: 1761, currency: 'RSD', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 3404, currency: 'RSD', transit: [3, 5]},
      {name: 'UPS Express Shipping', kind: 'express', price: 3756, currency: 'RSD', transit: [3, 5]},
    ],
  },
  {
    name: 'North Macedonia', countries: ['MK'],
    methods: [
      {name: 'Post of North Macedonia Postal Shipping', kind: 'standard', price: 925, currency: 'MKD', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 1788, currency: 'MKD', transit: [3, 5]},
      {name: 'UPS Express Shipping', kind: 'express', price: 1973, currency: 'MKD', transit: [3, 5]},
    ],
  },
  {
    name: 'Georgia', countries: ['GE'],
    methods: [
      {name: 'Georgian Post Postal Shipping', kind: 'standard', price: 44, currency: 'GEL', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 84, currency: 'GEL', transit: [3, 5]},
      {name: 'UPS Express Shipping', kind: 'express', price: 93, currency: 'GEL', transit: [3, 5]},
    ],
  },
  {
    name: 'Moldova', countries: ['MD'],
    methods: [
      {name: 'Poșta Moldovei Postal Shipping', kind: 'standard', price: 300, currency: 'MDL', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 581, currency: 'MDL', transit: [3, 5]},
      {name: 'UPS Express Shipping', kind: 'express', price: 641, currency: 'MDL', transit: [3, 5]},
    ],
  },
  {
    name: 'Europe 2', countries: ['BY'],
    methods: [{name: 'FedEx Express Shipping', kind: 'express', price: 29, currency: 'EUR', transit: [3, 5]}],
  },
  {
    name: 'Norway', countries: ['NO'],
    methods: [
      {name: 'Posten Postal Shipping', kind: 'standard', price: 96, currency: 'NOK', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 161, currency: 'NOK', transit: [3, 5]},
      {name: 'UPS Express Shipping', kind: 'express', price: 343, currency: 'NOK', transit: [3, 5]},
    ],
  },
  {
    name: 'Switzerland', countries: ['CH'],
    methods: [
      {name: 'Swiss Post Postal Shipping', kind: 'standard', price: 8, currency: 'CHF', transit: [5, 10]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 14, currency: 'CHF', transit: [3, 5]},
      {name: 'UPS Express Shipping', kind: 'express', price: 30, currency: 'CHF', transit: [3, 5]},
    ],
  },
  {
    name: 'Canada', countries: ['CA'],
    methods: [
      // Owner, 2026-10-23: FedEx Express is free from CA$120 after discounts (Canada zone rate conditions; the cart reward
      // bar uses the same CA$120). No postal option: Canada Post has no duties-paid service, so a postal parcel over CA$20
      // would be taxed at the door, against "Duties & taxes included".
      {name: 'FedEx Express Shipping', kind: 'express', price: 34, currency: 'CAD', transit: [3, 5], priceRule: {freeFrom: 135, below: 34}},
      {name: 'UPS Express Shipping', kind: 'express', price: 64, currency: 'CAD', transit: [3, 5]},
    ],
  },
  {
    name: 'Australia', countries: ['AU'],
    methods: [
      {name: 'Australia Post Standard Shipping', kind: 'standard', price: 14, currency: 'AUD', transit: [9, 15]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 43, currency: 'AUD', transit: [3, 5]},
    ],
  },
  {
    name: 'New Zealand', countries: ['NZ'],
    methods: [
      {name: 'NZ Post Standard Shipping', kind: 'standard', price: 36, currency: 'NZD', transit: [9, 15]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 71, currency: 'NZD', transit: [3, 5]},
      {name: 'UPS Express Shipping', kind: 'express', price: 107, currency: 'NZD', transit: [3, 5]},
    ],
  },
  {
    name: 'Japan', countries: ['JP'],
    methods: [
      {name: 'Japan Post Standard Shipping', kind: 'standard', price: 1500, currency: 'JPY', transit: [9, 15]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 4700, currency: 'JPY', transit: [3, 5]},
    ],
  },
  {
    name: 'Singapore', countries: ['SG'],
    methods: [
      {name: 'SingPost Standard Shipping', kind: 'standard', price: 12, currency: 'SGD', transit: [9, 15]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 38, currency: 'SGD', transit: [3, 5]},
      {name: 'UPS Express Shipping', kind: 'express', price: 58, currency: 'SGD', transit: [3, 5]},
    ],
  },
  {
    name: 'Hong Kong', countries: ['HK'],
    methods: [
      {name: 'Hongkong Post Standard Shipping', kind: 'standard', price: 120, currency: 'HKD', transit: [9, 15]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 200, currency: 'HKD', transit: [3, 5]},
    ],
  },
  {
    name: 'Malaysia', countries: ['MY'],
    methods: [
      {name: 'Pos Malaysia Standard Shipping', kind: 'standard', price: 62, currency: 'MYR', transit: [9, 15]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 104, currency: 'MYR', transit: [3, 5]},
    ],
  },
  {
    name: 'Taiwan', countries: ['TW'],
    methods: [
      {name: 'Chunghwa Post Standard Shipping', kind: 'standard', price: 490, currency: 'TWD', transit: [9, 15]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 810, currency: 'TWD', transit: [3, 5]},
    ],
  },
  {
    name: 'South Korea', countries: ['KR'],
    methods: [
      {name: 'Korea Post Standard Shipping', kind: 'standard', price: 20400, currency: 'KRW', transit: [9, 15]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 34100, currency: 'KRW', transit: [3, 5]},
    ],
  },
  // Latin America, opened 2026-10-22 (owner): the country's own post (sent from Türkiye with PTT) and FedEx Express,
  // 14.99 / 25.99 USD in the market currency (Costa Rica sells in USD: Shopify Payments has no CRC).
  {
    name: 'Chile', countries: ['CL'],
    methods: [
      {name: 'Correos de Chile Postal Shipping', kind: 'standard', price: 14990, currency: 'CLP', transit: [9, 15]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 25990, currency: 'CLP', transit: [4, 7]},
    ],
  },
  {
    name: 'Colombia', countries: ['CO'],
    methods: [
      {name: '4-72 Postal Shipping', kind: 'standard', price: 48900, currency: 'COP', transit: [9, 15]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 84900, currency: 'COP', transit: [4, 7]},
    ],
  },
  {
    name: 'Peru', countries: ['PE'],
    methods: [
      {name: 'Serpost Postal Shipping', kind: 'standard', price: 52.90, currency: 'PEN', transit: [9, 15]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 90.90, currency: 'PEN', transit: [4, 7]},
    ],
  },
  {
    name: 'Panama', countries: ['PA'],
    methods: [
      {name: 'Correos de Panamá Postal Shipping', kind: 'standard', price: 14.99, currency: 'USD', transit: [9, 15]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 25.99, currency: 'USD', transit: [4, 7]},
    ],
  },
  {
    name: 'Costa Rica', countries: ['CR'],
    methods: [
      {name: 'Correos de Costa Rica Postal Shipping', kind: 'standard', price: 14.99, currency: 'USD', transit: [9, 15]},
      {name: 'FedEx Express Shipping', kind: 'express', price: 25.99, currency: 'USD', transit: [4, 7]},
    ],
  },
  // Mexico, opened 2026-10-24 (owner): FedEx Express only, no postal option (slow post plus the SAT import tax would bring
  // complaints), MX$665 (= US$34.99 at Shopify's MXN rate that day) on every order; no free shipping and no cart reward bar.
  {
    name: 'Mexico', countries: ['MX'],
    methods: [{name: 'FedEx Express Shipping', kind: 'express', price: 665, currency: 'MXN', transit: [4, 7]}],
  },
];

export function zoneForCountry(countryCode) {
  return DELIVERY_ZONES.find(zone => zone.countries.includes(countryCode)) ?? null;
}

export function carrierForMethod(methodName) {
  if (methodName.startsWith('Pos Malaysia ')) return 'pos-malaysia';
  if (methodName.startsWith('Chunghwa Post ')) return 'chunghwa-post';
  if (methodName.startsWith('Korea Post ')) return 'korea-post';
  if (methodName.startsWith('Hongkong Post ')) return 'hongkong-post';
  if (methodName.startsWith('Swiss Post ')) return 'swiss-post';
  if (methodName.startsWith('Posten ')) return 'posten';
  if (methodName.startsWith('Pošta Slovenije ')) return 'posta-slovenije';
  if (methodName.startsWith('Slovenská pošta ')) return 'slovenska-posta';
  if (methodName.startsWith('Poșta Română ')) return 'posta-romana';
  if (methodName.startsWith('MaltaPost ')) return 'maltapost';
  if (methodName.startsWith('POST Luxembourg ')) return 'post-luxembourg';
  if (methodName.startsWith('Magyar Posta ')) return 'magyar-posta';
  if (methodName.startsWith('Latvijas Pasts ')) return 'latvijas-pasts';
  if (methodName.startsWith('Lietuvos paštas ')) return 'lietuvos-pastas';
  if (methodName.startsWith('Omniva ')) return 'omniva';
  if (methodName.startsWith('Hrvatska pošta ')) return 'hrvatska-posta';
  if (methodName.startsWith('Bulgarian Posts ')) return 'bulgarian-posts';
  if (methodName.startsWith('Packeta ')) return 'packeta';
  if (methodName.startsWith('Česká pošta ')) return 'ceska-posta';
  if (methodName.startsWith('MRW ')) return 'mrw';
  if (methodName.startsWith('CTT ')) return 'ctt';
  if (methodName.startsWith('Austrian Post ')) return 'austrian-post';
  if (methodName.startsWith('DHL ')) return 'dhl';
  if (methodName.startsWith('bpost ')) return 'bpost';
  if (methodName.startsWith('PostNL ')) return 'postnl';
  // Before 'Correos ' (Spain): the Latin American posts.
  if (methodName.startsWith('Correos de Chile ')) return 'correos-chile';
  if (methodName.startsWith('Correos de Panamá ')) return 'correos-panama';
  if (methodName.startsWith('Correos de Costa Rica ')) return 'correos-costa-rica';
  if (methodName.startsWith('4-72 ')) return '4-72';
  if (methodName.startsWith('Serpost ')) return 'serpost';
  if (methodName.startsWith('Correos ')) return 'correos';
  if (methodName.startsWith('Widect ')) return 'widect';
  if (methodName.startsWith('Poste Italiane ')) return 'poste-italiane';
  if (methodName.startsWith('Poczta Polska ')) return 'poczta-polska';
  if (methodName.startsWith('An Post ')) return 'an-post';
  if (methodName.startsWith('Posti ')) return 'posti';
  if (methodName.startsWith('Posta Postal ')) return 'posta-faroe';
  if (methodName.startsWith('Pošta Srbije ')) return 'posta-srbije';
  if (methodName.startsWith('Post of North Macedonia ')) return 'north-macedonia-post';
  if (methodName.startsWith('Georgian Post ')) return 'georgian-post';
  if (methodName.startsWith('Poșta Moldovei ')) return 'posta-moldovei';
  if (methodName.startsWith('Pósturinn ')) return 'posturinn';
  if (methodName.startsWith('BH Pošta ')) return 'bh-posta';
  if (methodName.startsWith('PostNord ')) return 'postnord';
  if (methodName.startsWith('ELTA Hellenic Post ')) return 'elta';
  if (methodName.startsWith('Cyprus Post ')) return 'cyprus-post';
  if (methodName.startsWith('La Poste Monaco ')) return 'la-poste-monaco';
  if (methodName.startsWith('La Poste ')) return 'la-poste';
  if (methodName.startsWith('GLS ')) return 'gls';
  if (methodName.startsWith('Deutsche Post ')) return 'deutsche-post';
  if (methodName.startsWith('Royal Mail ')) return 'royal-mail';
  return /^(USPS|FedEx|UPS|Evri)\b/.exec(methodName)?.[1].toLowerCase() ?? null;
}

// These are the merchant-confirmed last-mile partners for the generic Standard
// Shipping rate. The checkout method remains "Standard Shipping" because the
// origin carrier can vary before the parcel reaches the destination country.
export const STANDARD_LAST_MILE = {
  JP: 'Japan Post',
  AU: 'Australia Post',
  NZ: 'NZ Post',
  SG: 'SingPost',
};

// Monaco is treated as France for customs and VAT purposes.
// Same list as vat_countries in snippets/sa-tax-note.liquid (the product pages and the cart).
export const EU_VAT_TERRITORIES = new Set([
  'MC',
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]);

// The €150 / £135 limit as the shopper reads it in their checkout currency, from snippets/sa-tax-note.liquid
// (data-tax-rules limitLabels, e.g. "1 650 kr"): the amount the product pages and the cart compare prices against.
// Euro shoppers get "€150"; an unknown currency gets ''.
export function euLimitLabel(rules, currency) {
  if (typeof rules?.limitLabels !== 'string') return currency === 'EUR' || !currency ? '€150' : '';
  const entry = rules.limitLabels.split('|').find(pair => pair.split(':')[0] === (currency || 'EUR'));
  return entry ? entry.slice(entry.indexOf(':') + 1) : '';
}

export function taxNotice(countryCode, countryName, {euLimit = '€150'} = {}) {
  if (countryCode === 'HK') {
    return saText('delivery.tax_hk', 'Hong Kong does not levy VAT or general import tariffs on ordinary goods. The recipient is responsible for customs clearance, requested documents and any applicable import charges or carrier service fees.');
  }
  if (['MY', 'TW', 'KR'].includes(countryCode)) {
    let notice = saText('delivery.tax_asia', 'Import duties, taxes and carrier clearance fees may be charged by customs or the carrier in {country}. The recipient is responsible for customs clearance, requested documentation and any applicable import charges.', {country: countryName});
    if (countryCode === 'TW') notice += saText('delivery.tax_tw', ' For express shipments, complete EZ WAY real-name authorization or the customs authorization required by the carrier.');
    if (countryCode === 'KR') notice += saText('delivery.tax_kr', ' A valid Personal Customs Clearance Code (PCCC) is required for personal e-commerce imports. Provide it directly to the carrier when requested.');
    return notice;
  }
  // Mexico (owner, 2026-10-24): ~33.5% SAT import tax from the first dollar plus the carrier's clearance fee, paid by the
  // recipient; FedEx asks for an RFC or CURP; refused or uncollected orders are not refunded. The product page says it short
  // (sa_tax.mx_import) and the cart shorter (sa_tax.mx_cart), both in snippets/sa-tax-note.liquid.
  if (countryCode === 'MX') {
    return saText('delivery.tax_mx', 'Mexico charges ~33.5% SAT import tax from the first dollar, plus the carrier’s customs clearance fee. These charges are not included in our prices and are paid by the recipient at delivery. FedEx will ask for your RFC or CURP to clear customs. Orders refused or not collected because of these charges are non-refundable.');
  }
  if (countryCode === 'US') {
    return saText('delivery.tax_us', 'All U.S. orders ship with import fees included. No additional taxes or charges at delivery.');
  }
  if (countryCode === 'CA') {
    return saText('delivery.tax_ca', 'All Canadian orders ship with import fees included. No additional taxes or charges at delivery.');
  }
  if (countryCode === 'GB') {
    return saText('delivery.tax_gb', 'UK VAT is already included in our prices on orders up to £135 before VAT — no additional taxes or charges at delivery. Over £135 before VAT, no VAT is charged at checkout; import VAT and carrier fees are paid on delivery.');
  }
  if (EU_VAT_TERRITORIES.has(countryCode)) {
    return saText('delivery.tax_eu', 'For deliveries to {country}, prices marked “Taxes included” already include VAT. On orders up to {limit} before VAT, no additional taxes or charges at delivery. Over {limit} before VAT, no VAT is charged at checkout; import VAT and carrier fees are paid on delivery.', {country: countryName, limit: euLimit || '€150'});
  }
  // Owner, 2026-10-13: nothing about import charges for Australia (none are due at delivery up to A$1,000; the cart
  // names them above it). The popup hides its tax box.
  if (countryCode === 'AU') return '';
  return saText('delivery.tax_other', 'For {country}, import duties, taxes, or carrier fees may be due at delivery unless checkout clearly states they are included.', {country: countryName});
}

export function addBusinessDays(startDate, days) {
  if (!Number.isInteger(days) || days < 0) throw new RangeError('Business days must be a non-negative integer');
  const date = new Date(startDate.getTime());
  while (days > 0) {
    date.setUTCDate(date.getUTCDate() + 1);
    const weekday = date.getUTCDay();
    if (weekday !== 0 && weekday !== 6) days -= 1;
  }
  return date;
}

// The workshop continues production on Turkish public holidays, but cannot hand
// parcels to a carrier then. Treat half-day holidays as closed for dispatch:
// the estimate has no pickup time or carrier-specific half-day cutoff.
// Source (2026–2028): https://vakithesaplama.diyanet.gov.tr/icerik.php?icerik=158
// and the linked 2027/2028 official holiday lists. Refresh before 2029.
export const ORIGIN_DISPATCH_HOLIDAYS_THROUGH = 2028;
const ORIGIN_DISPATCH_HOLIDAYS = new Set([
  '2026-01-01', '2026-03-19', '2026-03-20', '2026-03-21', '2026-03-22',
  '2026-04-23', '2026-05-01', '2026-05-19', '2026-05-26', '2026-05-27',
  '2026-05-28', '2026-05-29', '2026-05-30', '2026-07-15', '2026-08-30',
  '2026-10-28', '2026-10-29',
  '2027-01-01', '2027-03-08', '2027-03-09', '2027-03-10', '2027-03-11',
  '2027-04-23', '2027-05-01', '2027-05-15', '2027-05-16', '2027-05-17',
  '2027-05-18', '2027-05-19', '2027-07-15', '2027-08-30', '2027-10-28',
  '2027-10-29',
  '2028-01-01', '2028-02-25', '2028-02-26', '2028-02-27', '2028-02-28',
  '2028-04-23', '2028-05-01', '2028-05-04', '2028-05-05', '2028-05-06',
  '2028-05-07', '2028-05-08', '2028-05-19', '2028-07-15', '2028-08-30',
  '2028-10-28', '2028-10-29',
]);

export function nextOriginDispatchDay(readyDate) {
  const date = new Date(readyDate.getTime());
  while (date.getUTCFullYear() <= ORIGIN_DISPATCH_HOLIDAYS_THROUGH) {
    const weekday = date.getUTCDay();
    if (weekday !== 0 && weekday !== 6
        && !ORIGIN_DISPATCH_HOLIDAYS.has(date.toISOString().slice(0, 10))) return date;
    date.setUTCDate(date.getUTCDate() + 1);
  }
  return null; // Do not display unverified dates after the holiday calendar expires.
}

export function todayAtFulfillmentOrigin(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const part = type => Number(parts.find(item => item.type === type)?.value);
  return new Date(Date.UTC(part('year'), part('month') - 1, part('day')));
}

export function deliveryWindow(method, processingMin, processingMax, today = todayAtFulfillmentOrigin()) {
  if (!method?.transit || method.transit.length !== 2) return null;
  const min = Number(processingMin);
  const max = Number(processingMax);
  if (!Number.isInteger(min) || !Number.isInteger(max) || min < 0 || max < min) return null;
  const firstDispatch = nextOriginDispatchDay(addBusinessDays(today, min));
  const lastDispatch = nextOriginDispatchDay(addBusinessDays(today, max));
  if (!firstDispatch || !lastDispatch) return null;
  return {
    earliest: addBusinessDays(firstDispatch, method.transit[0]),
    latest: addBusinessDays(lastDispatch, method.transit[1]),
  };
}

// Halloween: the latest estimate must land by Oct 30 so the mask is there for the 31st.
// The methods checkout offers for an order of this size (one item at the price shown on the page). A rate with
// availableBelow disappears from that amount on; an unknown price or another currency keeps every method.
export function methodsForPrice(zone, currency, itemPrice = NaN) {
  const known = Number.isFinite(itemPrice) && itemPrice > 0;
  return zone.methods.filter(method => !(known && method.availableBelow
    && currency === method.currency && itemPrice >= method.availableBelow));
}

// The method the card's date is for. From the free-express threshold ($95 US since 2026-10-30) checkout preselects
// free FedEx Express and has no USPS, so the headline is the FedEx date; below it, or when the price is unknown, the
// standard date as before. The price is the one shown on the page, so a $89.99 mask keeps the USPS date.
export function primaryMethod(zone, currency, itemPrice = NaN) {
  const methods = methodsForPrice(zone, currency, itemPrice);
  if (Number.isFinite(itemPrice) && itemPrice > 0) {
    const freeExpress = methods.find(method => method.kind === 'express' && method.priceRule
      && method.currency === currency && itemPrice >= method.priceRule.freeFrom);
    if (freeExpress) return freeExpress;
  }
  return methods.find(method => method.kind === 'standard') ?? methods[0];
}

// "$119.00" / "$1,234.50" → 119 / 1234.5; anything else → NaN. Europe (2026-10-24): Shopify formats each market's currency
// its own way — "€83,26", "375,70 zł", "1.135,00 kr", "2 401,00 Kč", "38 000 Ft" — so the last separator followed by one or
// two digits is the decimal mark and every other separator or space groups thousands.
export function parseShownPrice(text) {
  const match = /\d[\d.,\s\u00a0\u202f]*/.exec(String(text ?? ''));
  if (!match) return NaN;
  const raw = match[0].replace(/[\s\u00a0\u202f]+/g, '').replace(/[.,]+$/, '');
  const decimal = /[.,](\d{1,2})$/.exec(raw);
  const integer = (decimal ? raw.slice(0, decimal.index) : raw).replace(/[.,]/g, '');
  if (!/^\d+$/.test(integer)) return NaN;
  return Number(`${integer}.${decimal ? decimal[1] : '0'}`);
}

export function halloweenDeadline(today = todayAtFulfillmentOrigin()) {
  return new Date(Date.UTC(today.getUTCFullYear(), 9, 30));
}

// The cheapest method whose latest estimate still lands by the deadline, and the last day to order.
// Dates run on the workshop calendar (Istanbul); an evening order further west already falls on the
// next workshop day, so the cutoff shown to buyers is one calendar day earlier than the last workshop day.
export function halloweenDelivery(zone, processingMin, processingMax, today = todayAtFulfillmentOrigin(), deadline = halloweenDeadline(today)) {
  if (!zone?.methods?.length || today > deadline) return null;
  const cost = method => (method.kind === 'standard' && method.price === 0 ? -1 : (Number.isFinite(method.price) ? method.price : Infinity));
  const methods = zone.methods.filter(method => method.transit?.length === 2).sort((a, b) => cost(a) - cost(b));
  for (const method of methods) {
    const meets = day => {
      const window = deliveryWindow(method, processingMin, processingMax, day);
      return !!window && window.latest <= deadline;
    };
    if (!meets(today)) continue;
    let lastDay = today;
    const next = new Date(today.getTime());
    for (let i = 0; i < 60; i++) {
      next.setUTCDate(next.getUTCDate() + 1);
      if (!meets(next)) break;
      lastDay = new Date(next.getTime());
    }
    const cutoff = new Date(lastDay.getTime());
    cutoff.setUTCDate(cutoff.getUTCDate() - 1);
    if (cutoff >= today) return {method, cutoff};
  }
  return null;
}

// Halloween campaign only: when the standard estimate (the card headline) ends after the deadline but an express
// method still arrives by it, the latest express arrival date, so the card can say "Express by Oct 23".
// Shown for Asia-Pacific, whose 9–15 day standard rates miss Halloween from the start of the campaign.
// The US joined on 2026-10-11 (owner): from Oct 12 its free standard window ends after Oct 30 while FedEx Express still arrives.
export const HALLOWEEN_EXPRESS_HINT_COUNTRIES = new Set(['AU', 'NZ', 'JP', 'SG', 'HK', 'MY', 'TW', 'KR', 'US']);
export function halloweenExpressBy(zone, processingMin, processingMax, today = todayAtFulfillmentOrigin(), deadline = halloweenDeadline(today)) {
  const standard = zone?.methods?.find(method => method.kind === 'standard');
  const standardWindow = standard && deliveryWindow(standard, processingMin, processingMax, today);
  if (!standardWindow || standardWindow.latest <= deadline) return null;
  let latest = null;
  for (const method of zone.methods) {
    if (method.kind !== 'express') continue;
    const window = deliveryWindow(method, processingMin, processingMax, today);
    if (window && window.latest <= deadline && (!latest || window.latest < latest)) latest = window.latest;
  }
  return latest;
}

// The cheapest shipping price for a country where standard shipping is not free (the drawer shows "From CHF 8"),
// in the same wording as the popup's price column. Null where shipping is free or the price is unknown.
export function shippingFromLabel(zone, selectedCurrency, conversion = null) {
  // A method that is free from a threshold (Canada's FedEx from CA$120) counts at its price below it ("From CA$34").
  const priced = (zone?.methods ?? []).filter(method => Number.isFinite(method.price))
    .map(method => (method.priceRule ? {...method, price: method.priceRule.below, priceRule: undefined} : method));
  if (!priced.length || priced.some(method => method.kind === 'standard' && method.price === 0)) return null;
  const cheapest = priced.reduce((best, method) => (method.price < best.price ? method : best));
  const label = priceLabel(cheapest, selectedCurrency, conversion).main;
  return /\d/.test(label) ? label : null;
}

// Visitors from a country outside every market are shown the US store by Shopify. assets/sa-visitor-country.js
// detects their real country and words the line ("US addresses only — we don't ship to Brazil yet."); the US
// options stay, for shoppers who ship to a US address.
export function visitorNoShip(contextCountry) {
  const visitor = globalThis.SpaceArmoryNoShip;
  return visitor?.code && visitor.line && visitor.code !== contextCountry ? visitor : null;
}

export function formatDateWindow(window) {
  if (!window) return '';
  if (dateLocale() !== 'en-US') {
    const localized = new Intl.DateTimeFormat(dateLocale(), {month: 'short', day: 'numeric', timeZone: 'UTC'});
    if (typeof localized.formatRange === 'function') return localized.formatRange(window.earliest, window.latest);
  }
  const formatter = new Intl.DateTimeFormat('en-US', {month: 'short', day: 'numeric', timeZone: 'UTC'});
  const start = formatter.format(window.earliest);
  const end = formatter.format(window.latest);
  if (window.earliest.getUTCFullYear() === window.latest.getUTCFullYear()
      && window.earliest.getUTCMonth() === window.latest.getUTCMonth()) {
    return `${start}–${window.latest.getUTCDate()}`;
  }
  return `${start}–${end}`;
}

// Colombia (owner, 2026-10-24): the page's own money format for its currency, so the popup and the drawer's
// "Desde …" read "$48.900" like the prices around them instead of "COP 48,900". The sample is Shopify's money
// filter of 1234567.89 (as in snippets/space-armory-cart.liquid); a format this cannot read keeps the Intl one.
let shopMoney = null;
export function useShopMoneyFormat(currency, sample, hideZeroCents = false) {
  const parts = String(sample || '').match(/^(\D*)1(\D?)234(?:\2)567(\D)89(\D*)$/);
  shopMoney = currency && parts ? {currency, parts, hideZeroCents} : null;
}

function money(amount, currency) {
  if (shopMoney?.currency === currency) {
    const {parts, hideZeroCents} = shopMoney;
    const [whole, part] = Math.abs(amount).toFixed(2).split('.');
    const cents = hideZeroCents && part === '00' ? '' : parts[3] + part;
    return `${amount < 0 ? '-' : ''}${parts[1]}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, parts[2])}${cents}${parts[4]}`;
  }
  const digits = new Intl.NumberFormat('en-US', {style: 'currency', currency})
    .resolvedOptions().maximumFractionDigits;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency,
    minimumFractionDigits: Number.isInteger(amount) ? 0 : digits,
    maximumFractionDigits: digits,
  }).format(amount);
}

export function priceLabel(method, selectedCurrency, conversion = null) {
  if (method.priceRule) {
    if (selectedCurrency !== method.currency) return {main: saText('delivery.at_checkout', 'At checkout'), detail: ''};
    return {
      main: saText('delivery.free', 'FREE'),
      detail: saText('delivery.price_rule', 'on orders {from}+; {below} below',
        {from: money(method.priceRule.freeFrom, method.currency), below: money(method.priceRule.below, method.currency)}),
    };
  }
  if (method.price === 0) return {main: saText('delivery.free', 'FREE'), detail: ''};
  if (method.localPreview?.currency === selectedCurrency) {
    return {main: `≈${money(method.localPreview.amount, selectedCurrency)}`, detail: ''};
  }
  if (selectedCurrency !== method.currency) {
    if (conversion?.baseCurrency === method.currency
        && conversion.activeCurrency === selectedCurrency
        && Number.isFinite(conversion.rate) && conversion.rate > 0) {
      return {main: `≈${money(method.price * conversion.rate, selectedCurrency)}`, detail: saText('delivery.approx_local_price', 'Approx. local price')};
    }
    return {main: `${money(method.price, method.currency)} ${method.currency}`, detail: ''};
  }
  return {main: money(method.price, method.currency), detail: ''};
}

function element(tag, className, content = '') {
  const node = document.createElement(tag);
  node.className = className;
  node.textContent = content;
  return node;
}

// The popup's sheet: title, tax and returns notes, one row per shipping method with its carrier logo, dates and price,
// and the footnote. The product page's card fills it for the price shown on the page; the cart drawer's delivery line
// (assets/space-armory-cart.js) fills the same markup for the cart, passing orderTotal: what the shopper pays for the
// items after discounts, the amount the shipping rates' conditions use. A rule-priced method (FedEx free from $84)
// then reads plain FREE once the cart reaches it. Returns the out-of-market visitor, if any.
export function renderDeliverySheet(root, {zone, countryCode, countryName, currencyCode, baseCurrency, taxRules = null,
  processingMin, processingMax, price = NaN, orderTotal = NaN, logoFor}) {
  root.querySelector('[data-delivery-title]').textContent = saText('delivery.title', 'Delivery to {country}', {country: countryName});
  const taxCopy = root.querySelector('[data-delivery-tax]');
  const taxText = taxNotice(countryCode, countryName, {euLimit: euLimitLabel(taxRules, currencyCode)});
  // No notice (Australia): the whole tax box goes, title included.
  const taxBox = taxCopy.closest('.delivery-estimate__tax');
  if (taxBox) taxBox.hidden = !taxText;
  const emphasis = 'U.S. orders';
  const index = taxText.indexOf(emphasis);
  if (countryCode === 'US' && index >= 0) {
    taxCopy.replaceChildren(taxText.slice(0, index), element('strong', '', emphasis),
      taxText.slice(index + emphasis.length));
  } else {
    taxCopy.textContent = taxText;
  }

  if (EU_VAT_TERRITORIES.has(countryCode)) {
    taxCopy.append(document.createElement('br'), document.createElement('br'),
      saText('delivery.eu_duty', 'We cover the EU’s €3 customs duty — you won’t pay this charge.'));
  }

  const methods = root.querySelector('[data-delivery-methods]');
  methods.replaceChildren();
  for (const method of methodsForPrice(zone, currencyCode, price)) {
    const row = element('div', 'delivery-estimate__method');
    const details = element('div', 'delivery-estimate__method-details');
    const lastMile = method.kind === 'standard' && STANDARD_LAST_MILE[countryCode];
    const carrier = carrierForMethod(method.name) ?? (lastMile ? lastMile.toLowerCase().replaceAll(' ', '-') : null);
    const logoUrl = carrier && logoFor?.(carrier);
    if (logoUrl) {
      const logo = document.createElement('img');
      logo.className = 'delivery-estimate__carrier-logo';
      logo.src = logoUrl;
      if (carrier === 'posta-romana') logo.style.filter = 'brightness(0)';
      logo.alt = lastMile ? saText('delivery.local_delivery_by', 'Local delivery by {carrier}', {carrier: lastMile}) : '';
      logo.width = 66;
      logo.height = 40;
      if (lastMile) logo.classList.add('delivery-estimate__carrier-logo--last-mile');
      else logo.setAttribute('aria-hidden', 'true');
      logo.addEventListener('error', () => logo.remove(), {once: true});
      details.append(logo);
    }
    const copy = element('div', 'delivery-estimate__method-copy');
    const displayName = countryCode === 'AU' && lastMile && !method.name.startsWith(lastMile)
      ? `${lastMile} ${methodLabel(method.name)}` : methodLabel(method.name);
    copy.append(element('strong', 'delivery-estimate__method-name', displayName));
    const deliveryDates = deliveryWindow(method, processingMin, processingMax);
    const timing = deliveryDates
      ? saText('delivery.transit_line', '{dates} · {min}–{max} business days in transit',
        {dates: formatDateWindow(deliveryDates), min: method.transit[0], max: method.transit[1]})
      : saText('delivery.timing_varies', 'Timing varies by destination');
    copy.append(element('span', 'delivery-estimate__method-time', timing));
    const shopifyCurrency = window.Shopify?.currency;
    const conversion = {
      baseCurrency,
      activeCurrency: shopifyCurrency?.active,
      rate: Number(shopifyCurrency?.rate),
    };
    const label = Number.isFinite(orderTotal) && method.priceRule && currencyCode === method.currency
      && orderTotal >= method.priceRule.freeFrom ? {main: saText('delivery.free', 'FREE'), detail: ''} : priceLabel(method, currencyCode, conversion);
    const priceElement = element('span', 'delivery-estimate__method-price', label.main);
    if (label.detail) priceElement.append(element('small', '', label.detail));
    details.append(copy);
    row.append(details, priceElement);
    methods.append(row);
  }
  root.querySelector('[data-delivery-note-caveat]').textContent = deliveryWindow(primaryMethod(zone, currencyCode, price), processingMin, processingMax)
    ? saText('delivery.caveat_dates', 'Delivery dates may vary due to holidays or customs.')
    : saText('delivery.caveat_transit', 'Transit time varies by destination.');
  // A visitor from a country outside every market: the line goes on top of the US rates.
  const noShip = visitorNoShip(countryCode);
  if (noShip) methods.prepend(element('p', 'delivery-estimate__no-ship', noShip.line));
  return noShip;
}

if (typeof customElements !== 'undefined' && !customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', class extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('[data-delivery-open]');
      this.closeButton = this.querySelector('[data-delivery-close]');
      const zone = zoneForCountry(this.dataset.countryCode);
      // No estimate for this country: drop the reserved card space (delivery-estimate.css).
      if (!this.dialog || !this.opener || !this.closeButton || !zone?.methods.length) {
        this.dataset.unavailable = '';
        return;
      }
      this.zone = zone;
      if (this.dataset.moneySample) useShopMoneyFormat(this.dataset.currencyCode, this.dataset.moneySample, this.dataset.hideZeroCents === 'true');
      this.processingMin = Number(this.dataset.processingMin);
      this.processingMax = Number(this.dataset.processingMax);
      if (!Number.isInteger(this.processingMin) || !Number.isInteger(this.processingMax)
          || this.processingMin < 0 || this.processingMax < this.processingMin) {
        this.dataset.unavailable = '';
        return;
      }
      this.lifecycle = new AbortController();
      const options = {signal: this.lifecycle.signal};
      this.opener.addEventListener('click', () => this.open(), options);
      this.closest('product-info')?.addEventListener('click', event => {
        const trigger = event.target.closest?.('[data-delivery-note-open]');
        if (trigger) this.open(trigger);
      }, options);
      this.closeButton.addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('close', () => this.afterClose(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog) this.close();
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.render();
      }, options);
      document.addEventListener('sa:visitor-country', () => this.render(), options);
      // The price block is replaced when the shopper picks another variant; render again with its price.
      this.priceBlock = this.closest('product-info')?.querySelector('[id^="price-"]');
      if (this.priceBlock && typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver(() => {
          const price = this.shownPrice();
          if (price !== this.renderedPrice) this.render();
        });
        observer.observe(this.priceBlock, {childList: true, subtree: true, characterData: true});
        this.lifecycle.signal.addEventListener('abort', () => observer.disconnect());
      }
      this.render();
      this.hidden = false;
    }

    // The price the page shows for the selected variant (sale price included), in the page's currency, where a
    // method's price depends on it (US: FedEx free from $84; UK: FedEx free from £75, 2026-10-23; Europe from €90 or the
    // local amount, 2026-10-24). Elsewhere NaN:
    // the card keeps the standard date and the popup its plain rates.
    shownPrice() {
      const currency = this.dataset.currencyCode;
      const priced = this.zone?.methods?.some(method => method.priceRule && method.currency === currency);
      if (!priced || !this.priceBlock) return NaN;
      return parseShownPrice(this.priceBlock.querySelector('.price__regular .price-item--regular')?.textContent);
    }

    render() {
      const countryName = this.dataset.countryName || this.dataset.countryCode;
      this.renderedPrice = this.shownPrice();
      const primary = primaryMethod(this.zone, this.dataset.currencyCode, this.renderedPrice);
      const primaryWindow = deliveryWindow(primary, this.processingMin, this.processingMax);
      this.querySelector('[data-delivery-eyebrow]').textContent = primaryWindow
        ? saText('delivery.eyebrow_estimate', 'Delivery estimate') : saText('delivery.eyebrow_options', 'Shipping options');
      this.querySelector('[data-delivery-headline]').textContent = primaryWindow
        ? formatDateWindow(primaryWindow) : saText('delivery.shipping_to', 'Shipping to {country}', {country: countryName});
      const subtitle = this.querySelector('[data-delivery-subtitle]');
      const freeExpress = this.zone.methods.find(method => method.kind === 'express' && method.priceRule && method.currency === this.dataset.currencyCode);
      // Set pages v2 show "Free FedEx Express shipping" under the saving while this is true (snippets/sa-bundle-offer.liquid).
      this.toggleAttribute('data-free-express', !!(primary.kind === 'express' && primary.priceRule && this.renderedPrice >= primary.priceRule.freeFrom));
      if (primaryWindow && primary.kind === 'express' && primary.priceRule && this.renderedPrice >= primary.priceRule.freeFrom) {
        // From $84 / £75 / CA$120: the date above is FedEx's, "FREE FedEx Express".
        subtitle.replaceChildren(...saParts(saText('delivery.free_express_primary', '{free} {method}',
          {method: primary.name.replace(/ Shipping$/, '')}),
          {free: element('strong', '', saText('delivery.free', 'FREE'))}));
      } else if (primaryWindow && primary.kind === 'express' && primary.priceRule) {
        // Canada under CA$120 (no free standard method): "Canada · Free FedEx Express on CA$120+".
        subtitle.textContent = saText('delivery.express_free_from', '{country} · Free {method} on {from}+',
          {country: countryName, method: primary.name.replace(/ Shipping$/, ''), from: money(primary.priceRule.freeFrom, primary.currency)});
      } else if (primaryWindow && primary.kind === 'standard' && primary.price === 0 && freeExpress) {
        // "FREE Standard · Free FedEx Express on $84+" (owner, 2026-10-18); the Halloween hint below still replaces it while it runs.
        subtitle.replaceChildren(...saParts(saText('delivery.free_standard_free_express', '{free} Standard · Free {method} on {from}+',
          {method: freeExpress.name.replace(/ Shipping$/, ''), from: money(freeExpress.priceRule.freeFrom, freeExpress.currency)}),
          {free: element('strong', '', saText('delivery.free', 'FREE'))}));
      } else if (primaryWindow && primary.kind === 'standard' && primary.price === 0) {
        subtitle.replaceChildren(`${countryName} · `,
          ...saParts(saText('delivery.free_standard', '{free} Standard Shipping'), {free: element('strong', '', saText('delivery.free', 'FREE'))}));
      } else {
        subtitle.textContent = primaryWindow
          ? (primary.kind === 'standard'
            ? saText('delivery.standard_line', '{country} · Standard shipping', {country: countryName})
            : saText('delivery.express_line', '{country} · Express shipping', {country: countryName}))
          : saText('delivery.view_methods', 'View available methods and prices');
      }
      // Halloween: the headline is the standard estimate; when only express still arrives in time, say so here.
      const expressBy = this.dataset.halloween === '1' && HALLOWEEN_EXPRESS_HINT_COUNTRIES.has(this.dataset.countryCode)
        && primaryWindow && primary.kind === 'standard' && halloweenExpressBy(this.zone, this.processingMin, this.processingMax);
      if (expressBy) {
        // "Australia · Standard · Express by Oct 23": one line on a 375px phone; the hint never splits if it wraps.
        const day = new Intl.DateTimeFormat(dateLocale(), {month: 'short', day: 'numeric', timeZone: 'UTC'}).format(expressBy);
        const hint = saText('delivery.express_by', 'Express by {date}', {date: day}).replaceAll(' ', '\u00a0');
        if (primary.price === 0) {
          // Free standard (the US): keep the bold FREE the card shows, "FREE Standard · Express by Oct 26".
          subtitle.replaceChildren(...saParts(saText('delivery.free_standard_express', '{free} Standard · {hint}', {hint}),
            {free: element('strong', '', saText('delivery.free', 'FREE'))}));
        } else {
          subtitle.textContent = saText('delivery.standard_express_line', '{country} · Standard · {hint}', {country: countryName, hint});
        }
      }
      let rules = null;
      try { rules = JSON.parse(this.dataset.taxRules || 'null'); } catch {}
      // A visitor from a country outside every market: the US dates and rates stay, with the line as the card's
      // subtitle (it wraps like the usual one, never cut) and on top of the rates.
      // The shown price is also the order total of a one-item order: from $84 (£75) the popup's FedEx row says a
      // plain FREE, like the card above it and the cart drawer, instead of "FREE on orders $84+; $12 below" (2026-10-23).
      const noShip = renderDeliverySheet(this, {
        zone: this.zone, countryCode: this.dataset.countryCode, countryName, currencyCode: this.dataset.currencyCode,
        baseCurrency: this.dataset.baseCurrency, taxRules: rules, processingMin: this.processingMin,
        processingMax: this.processingMax, price: this.renderedPrice, orderTotal: this.renderedPrice,
        logoFor: carrier => this.getAttribute(`data-carrier-${carrier}-logo`),
      });
      if (noShip) subtitle.textContent = noShip.line;
      this.renderHalloween();
    }

    // The Halloween note (rendered next to this element during the campaign) names the last day to order
    // and the method that still arrives in time; it hides itself once no method can make it.
    renderHalloween(today = todayAtFulfillmentOrigin()) {
      const note = this.parentElement?.querySelector('.sa-halloween-delivery');
      if (!note) return;
      const plan = halloweenDelivery(this.zone, this.processingMin, this.processingMax, today);
      if (!plan) {
        note.hidden = true;
        return;
      }
      const day = new Intl.DateTimeFormat(dateLocale(), {weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC'}).format(plan.cutoff);
      const free = plan.method.kind === 'standard' && plan.method.price === 0;
      const button = note.querySelector('[data-delivery-note-open]') ?? element('button', 'sa-halloween-delivery-link');
      button.type = 'button';
      button.setAttribute('data-delivery-note-open', '');
      button.setAttribute('aria-haspopup', 'dialog');
      button.textContent = saText('delivery.halloween_see_options', 'See options');
      const parts = saParts(saText('delivery.halloween_order_by', '🎃 {halloween} delivery: order by {day}'), {halloween: element('span', '', 'Halloween'), day: element('strong', '', day)});
      if (free) {
        parts.push(saText('delivery.halloween_free', ' · free shipping. '));
      } else {
        const conversion = {baseCurrency: this.dataset.baseCurrency, activeCurrency: window.Shopify?.currency?.active, rate: Number(window.Shopify?.currency?.rate)};
        // The note keeps its "($12)" when FedEx is free from $84 (owner: leave the Halloween wording as it was).
        const price = plan.method.priceRule && this.dataset.currencyCode === plan.method.currency
          ? money(plan.method.priceRule.below, plan.method.currency) : priceLabel(plan.method, this.dataset.currencyCode, conversion).main;
        const priced = /\d/.test(price) ? ` (${price})` : '';
        const methodName = globalThis.SA_I18N ? methodLabel(plan.method.name) : plan.method.name.replace(/ Shipping$/, '');
        parts.push(saText('delivery.halloween_with', ' with {method}{price}. ', {method: methodName, price: priced}));
      }
      parts.push(button);
      note.replaceChildren(...parts);
      note.hidden = false;
      note.dataset.halloweenMethod = plan.method.kind;
    }

    open(trigger = this.opener) {
      if (this.dialog.open || document.querySelector('dialog[open]')) return;
      this.returnFocusTarget = trigger;
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.scrollLocked = true;
      this.querySelector('.delivery-estimate__sheet').scrollTop = 0;
      this.dialog.showModal();
      this.closeButton.focus({preventScroll: true});
    }

    close() {
      if (this.dialog.open) this.dialog.close();
      this.afterClose();
    }

    afterClose() {
      if (!this.scrollLocked || this.dialog.open) return;
      document.body.style.overflow = this.previousOverflow;
      this.scrollLocked = false;
      if (this.isConnected) (this.returnFocusTarget?.isConnected ? this.returnFocusTarget : this.opener).focus({preventScroll: true});
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = null;
      if (this.dialog?.open) this.dialog.close();
      this.afterClose();
    }
  });
}
